import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '../config';
import { setStoredOAuthTokens } from '../lib/authStorage';

/** Exchange authorization code for tokens and store them; then redirect to dashboard. */
export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(searchParams.get('error_description') || errorParam);
      setLoading(false);
      return;
    }
    if (!code) {
      setError('No authorization code received');
      setLoading(false);
      return;
    }
    const domain = (config.cognitoOAuthDomain || '').replace(/\/$/, '');
    const redirectUri = config.cognitoOAuthRedirectSignIn || `${window.location.origin}/auth/callback`;
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognitoClientId,
      code,
      redirect_uri: redirectUri,
    });
    fetch(`${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then((res) => {
        if (!res.ok) return res.text().then((t) => Promise.reject(new Error(t)));
        return res.json();
      })
      .then((data: { id_token?: string; refresh_token?: string }) => {
        const idToken = data.id_token;
        if (!idToken) throw new Error('No id_token in response');
        setStoredOAuthTokens(idToken, data.refresh_token);
        navigate('/dashboard', { replace: true });
        window.location.reload(); // so AuthContext picks up the stored token
      })
      .catch((err) => {
        setError(err.message || 'Sign-in failed');
      })
      .finally(() => setLoading(false));
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#6b7280' }}>Completing sign-in…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem' }}>
        <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: 8, color: '#b91c1c', marginBottom: '1rem' }}>
          {error}
        </div>
        <a href="/login" style={{ color: '#0d9488', fontWeight: 600 }}>
          Back to sign in
        </a>
      </div>
    );
  }
  return null;
}
